import { IQuoteRequestSchema, IQuoteRespSchema } from '@monetize/types/app';
import { FC, useMemo } from 'react';
import { MdCheck, MdClose } from 'react-icons/md';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MGrid,
} from '../../../../../components/Monetize';
import { useFlags } from '../../../../../services/launchDarkly';
import { QuoteDocumentsViewItem } from './QuoteDocumentsViewItem';
import { useQuoteDocumentsV2 } from './useQuoteDocumentsV2';

interface QuoteDocumentsViewProps {
  quote: IQuoteRespSchema;
  isReadOnly: boolean;
  onUpdate: (data: IQuoteRequestSchema) => void;
}

export const QuoteDocumentsView: FC<QuoteDocumentsViewProps> = ({
  quote,
  isReadOnly,
  onUpdate,
}: QuoteDocumentsViewProps) => {
  const { enableQuoteDocumentManagement, enableDocxSupport } = useFlags();
  const acceptedFileTypes = useMemo(
    () => (enableDocxSupport ? ['pdf', 'docx'] : ['pdf']),
    [enableDocxSupport],
  );

  const {
    msaData,
    sowData,
    pdfCoverData,
    pdfFooterData,

    msaDocument,
    sowDocument,
    pdfCoverDocument,
    pdfFooterDocument,

    handleActiveItemChange,
    handleAddItem,
    handleRemoveItem,
    handleGoToItem,
    isLoadingInitialData,
  } = useQuoteDocumentsV2({
    quote,
    onUpdate,
    enableFileUploadForMsa: !enableQuoteDocumentManagement,
  });

  return (
    <MFlex ml="2" w="full" mb="2">
      <MGrid
        templateColumns={[
          'repeat(1, 1fr)',
          'repeat(1, 1fr)',
          'repeat(1, 1fr)',
          'repeat(1, 1fr)',
          'repeat(4, 1fr)',
        ]}
        gap={4}
        flex="1"
        ml="6"
      >
        {!enableQuoteDocumentManagement && (
          <>
            <MBox flex="1" mr="4" minW="0">
              <QuoteDocumentsViewItem
                label="Cover"
                accept={acceptedFileTypes}
                activeItem={pdfCoverData.activeItem}
                linkUrl={pdfCoverData.linkUrl}
                savedItemText={pdfCoverData.savedItemText}
                disabled={pdfCoverData.saving}
                isLoading={pdfCoverData.saving || pdfCoverDocument.isFetching}
                isReadOnly={isReadOnly}
                onSetActiveItem={(item) =>
                  handleActiveItemChange('cover', item)
                }
                onAddItem={(data) => handleAddItem('cover', data)}
                onRemoveItem={() => handleRemoveItem('cover')}
                onGotoSavedItem={() => handleGoToItem('cover')}
                showControlButton
              />
            </MBox>
            <MBox flex="1" mr="4" minW="0">
              <QuoteDocumentsViewItem
                label="Footer"
                accept={acceptedFileTypes}
                activeItem={pdfFooterData.activeItem}
                linkUrl={pdfFooterData.linkUrl}
                savedItemText={pdfFooterData.savedItemText}
                disabled={pdfFooterData.saving}
                isLoading={pdfFooterData.saving || pdfFooterDocument.isFetching}
                isReadOnly={isReadOnly}
                onSetActiveItem={(item) =>
                  handleActiveItemChange('footer', item)
                }
                onAddItem={(data) => handleAddItem('footer', data)}
                onRemoveItem={() => handleRemoveItem('footer')}
                onGotoSavedItem={() => handleGoToItem('footer')}
                showControlButton
              />
            </MBox>
            <MBox flex="1" mr="4" minW="0">
              <QuoteDocumentsViewItem
                label="Statement of Work (SOW)"
                accept={acceptedFileTypes}
                activeItem={sowData.activeItem}
                linkUrl={sowData.linkUrl}
                savedItemText={sowData.savedItemText}
                disabled={sowData.saving || sowDocument.isFetching}
                isLoading={sowData.saving}
                isReadOnly={isReadOnly}
                onSetActiveItem={(item) => handleActiveItemChange('sow', item)}
                onAddItem={(data) => handleAddItem('sow', data)}
                onRemoveItem={() => handleRemoveItem('sow')}
                onGotoSavedItem={() => handleGoToItem('sow')}
                showControlButton
              />
            </MBox>
            <MBox flex="1" mr="4" minW="0">
              <QuoteDocumentsViewItem
                allowLink
                label="Master Service Agreement (MSA)"
                accept={acceptedFileTypes}
                activeItem={msaData.activeItem}
                linkUrl={msaData.linkUrl}
                savedItemText={msaData.savedItemText}
                disabled={msaData.saving || msaDocument.isFetching}
                isLoading={msaData.saving}
                isReadOnly={isReadOnly}
                onSetActiveItem={(item) => handleActiveItemChange('msa', item)}
                onAddItem={(data) => handleAddItem('msa', data)}
                onRemoveItem={() => handleRemoveItem('msa')}
                onGotoSavedItem={() => handleGoToItem('msa')}
                showControlButton
                saveBtnContent={({ value, error }) => {
                  return (
                    <>
                      <MCustomIconButton
                        variant="icon"
                        containerSize="8"
                        btnSize={5}
                        cursor="pointer"
                        icon={MdCheck}
                        aria-label="Save"
                        onClick={() => {
                          handleAddItem('msa', { type: 'link', value });
                        }}
                      />
                      <MCustomIconButton
                        variant="icon"
                        containerSize="8"
                        btnSize={5}
                        cursor="pointer"
                        icon={MdClose}
                        aria-label="Cancel"
                        onClick={() => {
                          handleActiveItemChange('msa', 'none');
                        }}
                      />
                    </>
                  );
                }}
              />
            </MBox>
          </>
        )}

        {enableQuoteDocumentManagement && (
          <MBox flex="1" mr="4" minW="0">
            <QuoteDocumentsViewItem
              allowLink
              allowFileUpload={false}
              label="Master Service Agreement (MSA)"
              activeItem={msaData.activeItem}
              linkUrl={msaData.linkUrl}
              savedItemText={msaData.savedItemText}
              disabled={msaData.saving || msaDocument.isFetching}
              isLoading={msaData.saving}
              isReadOnly={isReadOnly}
              onSetActiveItem={(item) => handleActiveItemChange('msa', item)}
              onAddItem={(data) => handleAddItem('msa', data)}
              onRemoveItem={() => handleRemoveItem('msa')}
              onGotoSavedItem={() => handleGoToItem('msa')}
              showControlButton
              saveBtnContent={({ value }) => {
                return (
                  <>
                    <MCustomIconButton
                      variant="icon"
                      containerSize="8"
                      btnSize={5}
                      cursor="pointer"
                      icon={MdCheck}
                      aria-label="Save"
                      onClick={() => {
                        handleAddItem('msa', { type: 'link', value });
                      }}
                    />
                    <MCustomIconButton
                      variant="icon"
                      containerSize="8"
                      btnSize={5}
                      cursor="pointer"
                      icon={MdClose}
                      aria-label="Cancel"
                      onClick={() => {
                        handleActiveItemChange('msa', 'none');
                      }}
                    />
                  </>
                );
              }}
            />
          </MBox>
        )}
      </MGrid>
    </MFlex>
  );
};
